import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { companyErrorKey } from './companyErrors';
import { issueOrgOwnerCode, useAdminOrgsCache } from '../../../api/admin-orgs';
import { publishToast } from '../../../components/feedback';

/**
 * Owner-code issuing for the create page and the «Коды» tab (AUT-403).
 *
 * The plaintext is held in THIS hook's `useState` and nowhere else: not the
 * TanStack cache (a plain call, not a query / mutation), not storage, not a
 * log. `clear()` drops it — the dialog calls it on close, so a reload or a
 * re-open never shows it again. Issuing invalidates the orgs cache so the
 * card's «не использован» state catches up.
 */
export interface UseOwnerCodeResult {
  code: string | null;
  busy: boolean;
  issue: (orgId: string) => Promise<boolean>;
  clear: () => void;
}

export const useOwnerCode = (): UseOwnerCodeResult => {
  const { t } = useTranslation();
  const cache = useAdminOrgsCache();
  const [code, setCode] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const issue = async (orgId: string): Promise<boolean> => {
    setBusy(true);
    try {
      const issued = await issueOrgOwnerCode(orgId);
      setCode(issued.code);
      return true;
    } catch (err: unknown) {
      publishToast({ severity: 'error', durationMs: 6000, message: t(companyErrorKey(err)) });
      return false;
    } finally {
      setBusy(false);
      void cache.invalidate();
    }
  };

  return { code, busy, issue, clear: () => setCode(null) };
};
